import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Lock, ArrowRight, Compass, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BOARD_IMAGE, gameIntro, howToPlay, remember, territories, gameElements } from '../data/lifeNinjaGame';

const TerritoryRow = ({ t, open, onToggle, unlocked }) => {
  const locked = !t.free && !unlocked;
  return (
    <div
      className="bg-white rounded-2xl border overflow-hidden transition-shadow"
      style={{ borderColor: open ? t.color + '55' : '#ECE7DD', boxShadow: open ? `0 8px 24px -12px ${t.color}66` : 'none' }}
      data-testid={`territory-${t.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 sm:p-5 text-left"
        data-testid={`territory-toggle-${t.id}`}
      >
        <span
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-heading text-lg text-white shadow-sm"
          style={{ backgroundColor: t.color }}
        >
          {t.level}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-xl sm:text-2xl" style={{ color: t.color }}>{t.name}</h3>
            {locked && <Lock size={14} className="text-stone-400" />}
          </div>
          <p className="font-body text-stone-500 text-sm italic">{t.tagline}</p>
        </div>
        <ChevronDown
          size={20}
          className="text-stone-400 transition-transform flex-shrink-0"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
            data-testid={`territory-panel-${t.id}`}
          >
            <div className="px-4 sm:px-5 pb-5 pt-1">
              <ul className="space-y-1.5">
                {t.stops.map((s) => (
                  <li
                    key={s.n}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                    style={{ backgroundColor: locked ? '#F6F4EF' : t.color + '0F' }}
                  >
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-body text-xs font-semibold"
                      style={{ backgroundColor: locked ? '#E4DED2' : t.color, color: locked ? '#9C9484' : '#fff' }}
                    >
                      {s.n}
                    </span>
                    <span className={`font-body text-sm flex-1 ${locked ? 'text-stone-400' : 'text-forest'}`}>
                      {s.title}
                    </span>
                    {!locked && s.link && (
                      <Link
                        to={s.link}
                        className="inline-flex items-center gap-1 font-body text-xs font-medium hover:underline whitespace-nowrap"
                        style={{ color: t.color }}
                        data-testid={`stop-link-${s.n}`}
                      >
                        {s.linkLabel || 'Explore'} <ArrowRight size={12} />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>

              {locked && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-forest/5 border border-forest/10 rounded-xl p-4">
                  <p className="flex-1 font-body text-sm text-stone-600">
                    <Lock size={13} className="inline mr-1.5 -mt-0.5 text-stone-400" />
                    The deeper territories are part of the full Life Ninja journey.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-1.5 bg-forest text-cream font-body text-sm px-4 py-2 rounded-full hover:bg-forest-dark transition-colors whitespace-nowrap"
                    data-testid={`territory-unlock-${t.id}`}
                  >
                    Unlock the journey <ArrowRight size={14} />
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LifeNinjaGamePage = () => {
  const { hasAccess } = useAuth();
  const unlocked = hasAccess('life-ninja-game');
  const [openId, setOpenId] = useState('survive');
  const [showElements, setShowElements] = useState(false);

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <div className="bg-cream min-h-screen pt-24 pb-16" data-testid="life-ninja-game-page">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6" data-testid="back-to-programs">
          <ArrowLeft size={18} />
          <span className="font-body text-sm">Back to Programs</span>
        </Link>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <img
            src={BOARD_IMAGE}
            alt="The Life Ninja Game board"
            className="w-full rounded-2xl shadow-lg mb-6"
            data-testid="board-image"
          />
          <p className="font-body text-terracotta text-sm tracking-wide uppercase mb-1">{gameIntro.subtitle}</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-2">{gameIntro.title}</h1>
          <p className="font-body text-stone-600 text-base sm:text-lg mb-4">{gameIntro.tagline}</p>
          <div className="bg-white rounded-xl border border-stone-100 p-4">
            <p className="font-body text-forest text-sm italic">{gameIntro.note}</p>
          </div>
        </motion.div>

        {/* How to Play */}
        <div className="mb-8 bg-white rounded-2xl border border-stone-100 p-5">
          <h2 className="font-heading text-lg text-forest mb-3 flex items-center gap-2">
            <Compass size={18} className="text-terracotta" /> How to Play
          </h2>
          <ul className="space-y-1.5">
            {howToPlay.map((step, i) => (
              <li key={i} className="font-body text-sm text-stone-600 flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" />
                {step}
              </li>
            ))}
          </ul>
        </div>

        {/* The 7 Territories */}
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="font-heading text-2xl text-forest">The 7 Territories</h2>
          <span className="font-body text-xs text-stone-400 italic">A map, not a straight line</span>
        </div>
        <div className="space-y-3 mb-10">
          {territories.map((t) => (
            <TerritoryRow key={t.id} t={t} open={openId === t.id} onToggle={() => toggle(t.id)} unlocked={unlocked} />
          ))}
        </div>

        {/* Game Elements legend */}
        <div className="mb-10 bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <button
            onClick={() => setShowElements((v) => !v)}
            className="w-full flex items-center justify-between p-5 text-left"
            data-testid="game-elements-toggle"
          >
            <div>
              <h2 className="font-heading text-lg text-forest">Game Elements</h2>
              <p className="font-body text-stone-500 text-sm">The pieces you'll meet along the way.</p>
            </div>
            <ChevronDown size={20} className="text-stone-400 transition-transform" style={{ transform: showElements ? 'rotate(180deg)' : 'none' }} />
          </button>
          <AnimatePresence initial={false}>
            {showElements && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="px-5 pb-5 grid sm:grid-cols-2 gap-2">
                  {gameElements.map((el) => (
                    <div key={el.name} className="bg-stone-50 rounded-xl p-3">
                      <p className="font-body text-sm font-semibold text-forest">{el.name}</p>
                      <p className="font-body text-xs text-stone-500">{el.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Remember */}
        <div className="bg-forest rounded-2xl p-6 text-center">
          <Heart size={22} className="text-terracotta mx-auto mb-3" />
          <div className="space-y-1">
            {remember.map((line, i) => (
              <p key={i} className={`font-body text-cream/90 ${i === remember.length - 1 ? 'font-heading text-lg text-cream mt-2' : 'text-sm'}`}>
                {line}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifeNinjaGamePage;
